import type { ReactElement } from 'react';
import { useEffect, useMemo } from 'react';
import Select, { type OptionType } from '@atlaskit/select';
import { defineMessages, useIntl } from 'react-intl';
import Heading from '@atlaskit/heading';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import {
  useSelectedOrg,
  useSelectedWorkspace,
} from '../../../Flow/ConsentContextProvider';

const messages = defineMessages({
  siteLabel: {
    id: 'oauth.twg.siteSelector.siteLabel',
    defaultMessage: 'Authorize access for:',
    description: 'Label for which site the user wants to authorize for TWG',
  },
  sitePlaceholder: {
    id: 'oauth.twg.siteSelector.sitePlaceholder',
    defaultMessage: 'Select a site',
    description: 'Placeholder for which sites user wants to authorize for TWG',
  },
});

const siteSelectStyles = xcss({
  maxWidth: '416px',
  width: '100%',
});

const SiteSelector: React.FC = (): ReactElement => {
  const { formatMessage } = useIntl();
  const { selectedWorkspace, setSelectedWorkspace } = useSelectedWorkspace();
  const { availableOrganizations, selectedOrg, setSelectedOrg } =
    useSelectedOrg();

  // Build a flat sorted list of all sites across all orgs, keeping track of which orgId each belongs to
  const siteToOrgId = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of availableOrganizations) {
      for (const site of org.availableWorkspaces ?? []) {
        map.set(site, org.orgId);
      }
    }
    return map;
  }, [availableOrganizations]);

  const workspaceOptions: OptionType[] = useMemo(
    () =>
      Array.from(siteToOrgId.keys())
        .sort()
        .map((site) => ({ label: site, value: site })),
    [siteToOrgId],
  );

  // Auto-select when there is exactly one site available
  useEffect(() => {
    if (workspaceOptions.length === 1 && !selectedWorkspace) {
      const site = String(workspaceOptions[0].value);
      const orgId = siteToOrgId.get(site) ?? null;
      setSelectedWorkspace(site);
      if (orgId !== selectedOrg) {
        void setSelectedOrg(orgId);
      }
    }
  }, [workspaceOptions, selectedWorkspace, selectedOrg, siteToOrgId, setSelectedWorkspace, setSelectedOrg]);

  // Find the single selected option
  const selectedOption =
    workspaceOptions.find((o) => o.value === selectedWorkspace) ?? null;
  const siteSelectorId = 'twg-site-selector';

  return (
    <Box xcss={siteSelectStyles}>
      <Stack space="space.100">
        <Box as="label" htmlFor={siteSelectorId}>
          <Heading size="small">{formatMessage(messages.siteLabel)}</Heading>
        </Box>
        <Select
          inputId={siteSelectorId}
          options={workspaceOptions}
          isSearchable={true}
          placeholder={formatMessage(messages.sitePlaceholder)}
          value={selectedOption}
          onChange={(option) => {
            const site = option ? String((option as OptionType).value) : null;
            if (!site) {
              setSelectedWorkspace(null);
              void setSelectedOrg(null);
            } else {
              const orgId = siteToOrgId.get(site) ?? null;
              setSelectedWorkspace(site);
              if (orgId !== selectedOrg) {
                void setSelectedOrg(orgId);
              }
            }
          }}
        />
      </Stack>
    </Box>
  );
};

export default SiteSelector;
